/**
 * @file Main entry forwarder route.
 *
 * When users hit /app this component determines where to redirect them based on
 * their account role or preferences.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { MeQuery } from '../../graphql/queries/account';
import RedirectWithStatus from './RedirectWithStatus';
import { getRoleBasename } from '../../util/role-definitions';
import {
  isCommanderOnly,
  getDefaultProRoleFromAccount,
} from '../../util/account';
import { accountAddRoles } from '../../graphql/mutations/account';
import ACCOUNT_ROLES from '../../constants/account-roles';
import FullScreenLoader from '../global/FullScreenLoader';

const propTypes = {
  loading: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
  addShutterbugRole: PropTypes.func.isRequired,
};

const defaultProps = {
  roles: [],
};

const EntryForwardComponent = ({ loading, user, addShutterbugRole }) => {
  const role = getDefaultProRoleFromAccount(user);
  const basename = getRoleBasename(role);

  useEffect(() => {
    if (user && user.roles && !role && !isCommanderOnly(user.roles)) {
      // Add SB role. No need to await, since we'll show
      // a Full screen loader.
      addShutterbugRole(user.id);
    }
  }, [role, user]);

  if (loading || !user || !user.roles) {
    return null;
  }

  // Redirect commanders
  if (isCommanderOnly(user.roles)) {
    return <RedirectWithStatus status={302} to="/command" />;
  }

  const accountWithRolesRedirectUrl = (() => {
    // If neither is the case, just default here again
    return `/app/${basename}`;
  })();

  if (role) {
    return <RedirectWithStatus status={301} to={accountWithRolesRedirectUrl} />;
  }

  return <FullScreenLoader />;
};

EntryForwardComponent.propTypes = propTypes;
EntryForwardComponent.defaultProps = defaultProps;

const withUserData = graphql(MeQuery, {
  props: ({ data: { loading, me } }) => ({
    loading,
    roles: me?.roles,
    user: me,
  }),
  skip: ownProps => !ownProps.authenticated,
});

const withAccountAddRolesMutation = graphql(accountAddRoles, {
  props: ({ mutate }) => ({
    addShutterbugRole: accountId =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            // note: ACCOUNT_ROLES enum contains lowercase, but API requires uppercase
            roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
          },
        },
      }),
  }),
});

export { EntryForwardComponent };

export default compose(
  withUserData,
  withAccountAddRolesMutation,
)(EntryForwardComponent);
